/**
 * Notification Navigation Utilities
 *
 * Provides intelligent URL processing for notification redirection
 * integrating with the modern React Router v6 routing system.
 */

import { NavigationRoutes, RouteBuilders } from '@/router/navigation.types'

const APP_ORIGIN = window.location.origin
const SAFE_PROTOCOLS = new Set(['http:', 'https:'])

export type NavigationResult =
    | { type: 'internal'; route: string }
    | { type: 'external'; route: string }
    | { type: 'invalid'; error: string }

/**
 * Determines if a URL is internal to the application or external
 */
export function isInternalUrl(url: string): boolean {
    try {
        // Handle relative URLs (they're internal)
        if (url.startsWith('/')) {
            return true
        }

        // Handle absolute URLs
        const urlObj = new URL(url)
        return urlObj.origin === APP_ORIGIN
    } catch {
        // Invalid URL format, treat as external for safety
        return false
    }
}

/**
 * Extract full path including query and hash from URL (internal URLs only)
 */
function extractPath(url: string): string | null {
    try {
        if (url.startsWith('/')) {
            // Relative URL - normalize pathname but preserve query/hash
            const [pathname, searchHash = ''] = url.split(/(?=[?#])/)
            const normalized = pathname.replace(/\/+/g, '/').replace(/(.)\/$/, '$1') || '/'
            return normalized + searchHash
        } else {
            // Absolute URL - extract full path if same origin
            const urlObj = new URL(url)
            if (urlObj.origin === APP_ORIGIN) {
                const fullPath = urlObj.pathname + urlObj.search + urlObj.hash
                const [pathname, searchHash = ''] = fullPath.split(/(?=[?#])/)
                const normalized = pathname.replace(/\/+/g, '/').replace(/(.)\/$/, '$1') || '/'
                return normalized + searchHash
            }
        }
        return null
    } catch {
        return null
    }
}

/**
 * Normalize pathname (fix double slashes, handle trailing slashes)
 */
function _normalizePath(pathname: string): string {
    const normalized = (pathname || '').trim().replace(/\/+/g, '/')
    const noTrailing = normalized !== '/' ? normalized.replace(/\/$/, '') : normalized
    return noTrailing.startsWith('/') ? noTrailing : `/${noTrailing}`
}

/**
 * Processes a notification URL for navigation
 * Maps legacy URLs to new route structure and validates internal routes
 */
export function processNotificationUrl(url: string): NavigationResult {
    if (!url?.trim()) {
        return {
            type: 'invalid',
            error: 'Empty URL provided',
        }
    }

    // Check if it's an external URL with security validation
    if (!isInternalUrl(url)) {
        try {
            const urlObj = new URL(url)
            if (!SAFE_PROTOCOLS.has(urlObj.protocol)) {
                return {
                    type: 'invalid',
                    error: 'Unsafe URL protocol',
                }
            }
            return {
                type: 'external',
                route: urlObj.toString(),
            }
        } catch {
            return {
                type: 'invalid',
                error: 'Malformed URL',
            }
        }
    }

    // Extract full path from internal URL
    const fullPath = extractPath(url)
    if (!fullPath) {
        return {
            type: 'invalid',
            error: 'Invalid internal URL',
        }
    }

    // Handle legacy URL mapping
    const mappedPath = mapLegacyUrl(fullPath)

    // Skip validation - let the router handle invalid routes
    // This prevents notification URLs from being blocked by overly strict validation
    return {
        type: 'internal',
        route: mappedPath,
    }
}

/**
 * Maps legacy organization-scoped URLs to new route structure
 * Preserves query parameters and hash fragments
 */
function mapLegacyUrl(path: string): string {
    // Split pathname from query/hash to handle them separately
    const [pathname, searchHash = ''] = path.split(/(?=[?#])/)

    const orgPattern = /^\/organization\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i
    const withoutOrg = pathname.replace(orgPattern, '') || '/'

    // Handle specific legacy route mappings
    const routeMappings: Record<string, string> = {
        '/team-directory': NavigationRoutes.TEAM,
        '/hr-uploads': NavigationRoutes.UPLOAD_HRIS,
        '/manager-uploads': NavigationRoutes.UPLOAD_PERFORMANCE,
        '/employee-uploads': NavigationRoutes.UPLOAD_DOCUMENT,
        '/notifications': NavigationRoutes.NOTIFICATION_SETTINGS,
        '/help-docs': NavigationRoutes.DOCUMENTATION,
    }

    // Apply direct mapping if found
    if (routeMappings[withoutOrg]) {
        return routeMappings[withoutOrg] + searchHash
    }

    const dynamicMappings = [
        {
            pattern: /^\/team-directory\/member\/([^/?#]+)/i,
            mapper: (match: RegExpMatchArray) => RouteBuilders.teamMemberDetail(decodeURIComponent(match[1])),
        },
        {
            pattern: /^\/(hr|manager|employee)-uploads\/([^/?#]+)/i,
            mapper: (match: RegExpMatchArray) => RouteBuilders.uploadDetails(decodeURIComponent(match[2])),
        },
    ]

    for (const { pattern, mapper } of dynamicMappings) {
        const match = withoutOrg.match(pattern)
        if (match) {
            return mapper(match) + searchHash
        }
    }

    return withoutOrg + searchHash
}

/**
 * Gets a user-friendly error message for navigation failures
 */
export function getNavigationErrorMessage(error: string): string {
    if (error.includes('Route not found')) {
        return "The page you're trying to access could not be found."
    }

    if (error.includes('Invalid URL format') || error.includes('Malformed URL')) {
        return 'The link appears to be corrupted. Please try again.'
    }

    if (error.includes('Empty URL')) {
        return 'No destination was provided for this notification.'
    }

    if (error.includes('Unsafe URL protocol')) {
        return 'This link is not safe to open.'
    }

    return 'Unable to navigate to the requested page. Please try again.'
}
